import React from "react";

const TechnologyStack = () => {
  const technologies = [
    {
      category: "Frontend Automation",
      icon: "🌐",
      tools: [
        {
          name: "Playwright",
          description: "Modern end-to-end testing framework",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Selenium WebDriver",
          description: "Cross-browser automation standard",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Cypress",
          description: "Developer-friendly testing framework",
          expertise: "Advanced",
          color: "text-blue-400",
        },
      ],
    },
    {
      category: "Programming Languages",
      icon: "💻",
      tools: [
        {
          name: "TypeScript",
          description: "Type-safe JavaScript for scalable automation",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Java",
          description: "Enterprise-grade automation development",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "JavaScript",
          description: "Dynamic scripting for web automation",
          expertise: "Expert",
          color: "text-blue-400",
        },
      ],
    },
    {
      category: "API Testing",
      icon: "🔌",
      tools: [
        {
          name: "REST Assured",
          description: "Java-based API testing framework",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Postman/Newman",
          description: "API development and testing platform",
          expertise: "Expert",
          color: "text-blue-400",
        },
        {
          name: "Insomnia",
          description: "Modern API client and testing tool",
          expertise: "Intermediate",
          color: "text-yellow-400",
        },
      ],
    },
    {
      category: "Test Management",
      icon: "🧪",
      tools: [
        {
          name: "TestNG",
          description: "Powerful testing framework for Java",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "JUnit",
          description: "Standard unit testing framework",
          expertise: "Expert",
          color: "text-blue-400",
        },
        {
          name: "Jest",
          description: "JavaScript testing framework",
          expertise: "Advanced",
          color: "text-blue-400",
        },
      ],
    },
    {
      category: "CI/CD Integration",
      icon: "🚀",
      tools: [
        {
          name: "GitHub Actions",
          description: "Cloud-native CI/CD automation",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Jenkins",
          description: "Open-source automation server",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "Docker",
          description: "Containerized test execution",
          expertise: "Advanced",
          color: "text-blue-400",
        },
      ],
    },
    {
      category: "Reporting & Analytics",
      icon: "📊",
      tools: [
        {
          name: "Allure Reports",
          description: "Beautiful and informative test reports",
          expertise: "Expert",
          color: "text-green-400",
        },
        {
          name: "ExtentReports",
          description: "Rich HTML test reporting",
          expertise: "Expert",
          color: "text-blue-400",
        },
        {
          name: "Custom Dashboards",
          description: "Tailored analytics and metrics",
          expertise: "Expert",
          color: "text-green-400",
        },
      ],
    },
  ];

  const aiCapabilities = [
    {
      title: "Intelligent Test Generation",
      description:
        "AI-powered Page Object Model and test case generation from requirements",
      icon: "🧠",
    },
    {
      title: "Smart Element Detection",
      description:
        "Advanced DOM analysis and robust locator strategy generation",
      icon: "🎯",
    },
    {
      title: "Natural Language Processing",
      description:
        "Convert user stories and acceptance criteria into executable tests",
      icon: "📝",
    },
    {
      title: "Adaptive Learning",
      description:
        "Continuous improvement through ML-based pattern recognition",
      icon: "📈",
    },
  ];

  const getExpertiseColor = (expertise) => {
    switch (expertise) {
      case "Expert":
        return "bg-green-500";
      case "Advanced":
        return "bg-blue-500";
      case "Intermediate":
        return "bg-yellow-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <section id="technology" className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Technology <span className="text-gradient">Expertise</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Leveraging cutting-edge tools and frameworks to deliver scalable,
            maintainable automation solutions with enterprise-grade reliability.
          </p>
        </div>

        {/* AI Capabilities Section */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            🤖 AI-Powered Automation Capabilities
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiCapabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-gradient-to-br bg-gray-900/50 rounded-xl p-6 border border-gray-800/50 hover:border-blue-400/50 transition-all duration-300"
              >
                <div className="text-3xl mb-4">{capability.icon}</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  {capability.title}
                </h4>
                <p className="text-gray-300 text-sm">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Technology Stack Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {technologies.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="text-3xl">{category.icon}</div>
                <h3 className="text-xl font-bold text-white">
                  {category.category}
                </h3>
              </div>

              <div className="space-y-4">
                {category.tools.map((tool, toolIndex) => (
                  <div
                    key={toolIndex}
                    className="bg-gray-900/50 rounded-lg p-4 border border-gray-700/30"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`font-semibold ${tool.color}`}>
                        {tool.name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getExpertiseColor(
                            tool.expertise
                          )}`}
                        >
                          {tool.expertise}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm">{tool.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Philosophy */}
        <div className="mt-20">
          <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-8 md:p-12 border border-gray-700/50">
            <h3 className="text-3xl font-bold text-white text-center mb-6">
              Our Integration Philosophy
            </h3>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl mb-4">🔗</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Seamless Integration
                </h4>
                <p className="text-gray-400 text-sm">
                  Every tool is carefully selected and integrated to work
                  harmoniously within your existing development ecosystem.
                </p>
              </div>
              <div>
                <div className="text-4xl mb-4">📐</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Scalable Architecture
                </h4>
                <p className="text-gray-400 text-sm">
                  Built with growth in mind, our solutions scale effortlessly
                  from startup MVPs to enterprise-grade applications.
                </p>
              </div>
              <div>
                <div className="text-4xl mb-4">🛡️</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Future-Proof Solutions
                </h4>
                <p className="text-gray-400 text-sm">
                  Modern, maintainable code that evolves with your business and
                  adapts to emerging technologies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyStack;
